import { compareByRank } from '../personnel/crew.js';
import type {
  CrewListCardParticulars,
  CrewListDirection,
  CrewListDocType,
  CrewListDraft,
  CrewListDraftPerson,
  CrewListSaveInput,
  CrewListVoyageOption,
} from './types.js';

/**
 * Yeni Crew List düzenleyicisinin saf durumu (ADR 0130 §B2): taslaktan (gemideki kişiler, gemi
 * ve seferler) başlar; alanlar metin olarak tutulur, kaydederken boş metin `null` olur (domain
 * şeması da aynı şeyi yapar). Kişiler varsayılan hepsi seçili ve rütbe sırasında.
 */

export interface EditorRow {
  userId: string;
  include: boolean;
  familyName: string;
  givenNames: string;
  rank: string;
  nationality: string;
  birthDate: string | null;
  birthPlace: string;
  docType: CrewListDocType;
  docNumber: string;
  /** İkinci belge (04.11 notları, karar 13); yalnız iki belgeyi kabul eden sunucuda. */
  secondDocType: CrewListDocType | null;
  secondDocNumber: string;
  /** Kişinin kayıtlı belge numaraları (tür değişince numara buradan gelir). */
  numbers: { passport: string | null; seaman_book: string | null };
  /** Personel kartındaki değerler ("Edited" işareti; ADR 0131 §2). */
  card?: CrewListCardParticulars | null;
}

export interface EditorHeader {
  vesselName: string;
  imo: string;
  callSign: string;
  voyageNo: string;
  port: string;
  date: string | null;
  flag: string;
  lastPort: string;
}

export interface EditorState {
  direction: CrewListDirection;
  voyageId: string | null;
  header: EditorHeader;
  rows: EditorRow[];
}

const text = (value: string | null | undefined) => value ?? '';

/** Belge türü: pasaport numarası varsa pasaport, yoksa cüzdan numarası varsa cüzdan. */
function defaultDocType(person: CrewListDraftPerson): CrewListDocType {
  if (person.passportNo) return 'passport';
  return person.seamanBookNo ? 'seaman_book' : 'passport';
}

function rowOf(person: CrewListDraftPerson, twoDocuments: boolean): EditorRow {
  const docType = defaultDocType(person);
  const numbers = { passport: person.passportNo, seaman_book: person.seamanBookNo };
  // Kişi kartında ikisi de varsa ikisi seçili gelir (karar 13): pasaport üstte, cüzdan altta
  const both = twoDocuments && person.passportNo !== null && person.seamanBookNo !== null;
  return {
    userId: person.userId,
    include: true,
    familyName: person.familyName,
    givenNames: person.givenNames,
    rank: text(person.rank),
    nationality: text(person.nationality),
    birthDate: person.birthDate,
    birthPlace: text(person.birthPlace),
    docType,
    docNumber: text(numbers[docType]),
    secondDocType: both ? 'seaman_book' : null,
    secondDocNumber: both ? text(numbers.seaman_book) : '',
    numbers,
    ...(person.card ? { card: person.card } : {}),
  };
}

export type CardField = keyof CrewListCardParticulars;

/**
 * Personel kartından gelip listede elle değiştirilen alanlar (ADR 0131 §2): kart değeri olan ve
 * listedeki değeri (kırpılmış) ondan farklı olan alanlar. Kart değişmez; değişiklik yalnız bu
 * listededir.
 */
export function editedFromCard(row: EditorRow): CardField[] {
  const card = row.card;
  if (!card) return [];
  const value = (field: CardField) => (row[field] ?? '').trim() || null;
  return (['nationality', 'birthDate', 'birthPlace'] as const).filter(
    (field) => card[field] !== null && value(field) !== card[field],
  );
}

/**
 * Seferden gelen üst alanlar: varışta liman seferin varış limanı ve ETA günü, son liman kalkış
 * limanı; kalkışta liman kalkış limanı ve ETD günü (son liman bilinmez, boş). Sefer yoksa liman
 * ve tarih boş kalır; kullanıcı yazar.
 */
export function voyageFields(
  voyage: CrewListVoyageOption | null,
  direction: CrewListDirection,
): Pick<EditorHeader, 'voyageNo' | 'port' | 'date' | 'lastPort'> {
  if (!voyage) return { voyageNo: '', port: '', date: null, lastPort: '' };
  return direction === 'arrival'
    ? {
        voyageNo: voyage.voyageNo,
        port: text(voyage.toPort),
        date: voyage.eta,
        lastPort: text(voyage.fromPort),
      }
    : { voyageNo: voyage.voyageNo, port: text(voyage.fromPort), date: voyage.etd, lastPort: '' };
}

export function initialEditor(draft: CrewListDraft, locale = 'en'): EditorState {
  const direction: CrewListDirection = 'arrival';
  const voyage = draft.voyages[0] ?? null;
  const people = [...draft.crew].sort((a, b) =>
    compareByRank(
      { fullName: a.fullName, role: a.rank },
      { fullName: b.fullName, role: b.rank },
      locale,
    ),
  );
  return {
    direction,
    voyageId: voyage?.id ?? null,
    header: {
      vesselName: draft.vessel.name,
      imo: text(draft.vessel.imo),
      callSign: text(draft.vessel.callSign),
      flag: text(draft.vessel.flag),
      ...voyageFields(voyage, direction),
    },
    rows: people.map((person) => rowOf(person, draft.twoDocuments === true)),
  };
}

/** Sefer ya da yön değişince seferden gelen alanlar yeniden dolar; gemi alanları korunur. */
export function withVoyage(
  state: EditorState,
  draft: CrewListDraft,
  voyageId: string | null,
  direction: CrewListDirection,
): EditorState {
  const voyage = draft.voyages.find((v) => v.id === voyageId) ?? null;
  return {
    ...state,
    direction,
    voyageId: voyage?.id ?? null,
    header: { ...state.header, ...voyageFields(voyage, direction) },
  };
}

/** Satırı bir yukarı (`-1`) ya da aşağı (`+1`) taşır; uçta değişmez. */
export function moveRow(rows: readonly EditorRow[], index: number, step: -1 | 1): EditorRow[] {
  const target = index + step;
  if (target < 0 || target >= rows.length) return [...rows];
  const next = [...rows];
  const [row] = next.splice(index, 1);
  next.splice(target, 0, row as EditorRow);
  return next;
}

/** Rütbe sırasına döner (taşımaları geri alır). */
export function sortRows(rows: readonly EditorRow[], locale = 'en'): EditorRow[] {
  return [...rows].sort((a, b) =>
    compareByRank(
      { fullName: `${a.familyName} ${a.givenNames}`, role: a.rank || null },
      { fullName: `${b.familyName} ${b.givenNames}`, role: b.rank || null },
      locale,
    ),
  );
}

/** Belge türü değişince numara kişinin o türdeki kayıtlı numarasıyla dolar. */
export function withDocType(row: EditorRow, docType: CrewListDocType): EditorRow {
  return { ...row, docType, docNumber: text(row.numbers[docType]) };
}

const DOC_ORDER: readonly CrewListDocType[] = ['passport', 'seaman_book'];

/** Satırda seçili belgeler ve numaraları (birincil önce). */
export function selectedDocs(row: EditorRow): Array<{ type: CrewListDocType; number: string }> {
  return [
    { type: row.docType, number: row.docNumber },
    ...(row.secondDocType ? [{ type: row.secondDocType, number: row.secondDocNumber }] : []),
  ];
}

/**
 * İki onay kutusu (04.11 notları, karar 13): belge eklenir ya da çıkarılır. Sıra her zaman
 * pasaport, cüzdan; ilki birincil olur. Yazılmış numara korunur, yeni eklenen belgenin numarası
 * kişi kartından gelir. Son belge çıkarılamaz (kutu 11 boş kalmaz).
 */
export function withDocSelection(
  row: EditorRow,
  docType: CrewListDocType,
  checked: boolean,
): EditorRow {
  const current = new Map(selectedDocs(row).map((doc) => [doc.type, doc.number]));
  if (checked) current.set(docType, current.get(docType) ?? text(row.numbers[docType]));
  else if (current.size > 1) current.delete(docType);
  else return row;
  const docs = DOC_ORDER.filter((type) => current.has(type)).map((type) => ({
    type,
    number: current.get(type) ?? '',
  }));
  const [first, second] = docs;
  if (!first) return row;
  return {
    ...row,
    docType: first.type,
    docNumber: first.number,
    secondDocType: second?.type ?? null,
    secondDocNumber: second?.number ?? '',
  };
}

/** Seçili belgenin numarası yazılır. */
export function withDocNumber(row: EditorRow, docType: CrewListDocType, value: string): EditorRow {
  if (row.docType === docType) return { ...row, docNumber: value };
  if (row.secondDocType === docType) return { ...row, secondDocNumber: value };
  return row;
}

const orNull = (value: string) => (value.trim() ? value : null);

/** Kaydedilecek liste: yalnız seçili kişiler, görünen sırayla. */
export function saveInput(state: EditorState): CrewListSaveInput {
  const h = state.header;
  return {
    direction: state.direction,
    voyageId: state.voyageId,
    header: {
      vesselName: h.vesselName,
      imo: orNull(h.imo),
      callSign: orNull(h.callSign),
      voyageNo: orNull(h.voyageNo),
      port: orNull(h.port),
      date: h.date,
      flag: orNull(h.flag),
      lastPort: orNull(h.lastPort),
    },
    rows: state.rows
      .filter((row) => row.include)
      .map((row) => ({
        userId: row.userId,
        familyName: row.familyName,
        givenNames: orNull(row.givenNames),
        rank: orNull(row.rank),
        nationality: orNull(row.nationality),
        birthDate: row.birthDate,
        birthPlace: orNull(row.birthPlace),
        docType: row.docType,
        docNumber: orNull(row.docNumber),
        // Tek belgeli satır eski biçimde gider (eski gemi sunucusu ikinci belge alanını reddeder)
        ...(row.secondDocType
          ? { secondDocType: row.secondDocType, secondDocNumber: orNull(row.secondDocNumber) }
          : {}),
      })),
  };
}
